import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowRight,
  FileText,
  GraduationCap,
  Plus,
  Save,
  Trash2,
  UserPlus,
  Video,
} from 'lucide-react'
import { Card } from '@/components/ui'
import { PedagogyBackLink } from '@/components/pedagogy/PedagogyBackLink'
import { SearchableSelect } from '@/components/pedagogy/SearchableSelect'
import { PedagogyAccessErrorCard } from '@/components/pedagogy/PedagogyAccessStates'
import { usePedagogyAccess } from '@/hooks/usePedagogyAccess'
import { pedagogyPaths } from '@/lib/pedagogyRoutes'
import {
  createFranchiseTeacher,
  findFranchiseTeacherByContact,
  listTeacherRegistryReferenceData,
  type FranchiseTeacherContactMatch,
  type TeacherEntryMode,
  type TeacherQualification,
  type TeacherRegistryReferenceData,
} from '@/services/franchiseTeacherService'

type AssignmentDraft = {
  id: string
  partnerNumber: string
  cityId: string
  startsOn: string
}

type VideoDraft = {
  videoUrl: string
  charismaScore: string
  professionalismScore: string
  clarityScore: string
  emotionalityScore: string
  decision: '' | 'approved' | 'rejected'
  comment: string
}

function parseScore(value: string): number | null {
  if (!value) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function normalizeTelegramUsername(value: string): string {
  return value.trim().replace(/^@+/, '')
}

function localIsoDate(): string {
  const now = new Date()
  const offset = now.getTimezoneOffset() * 60_000
  return new Date(now.getTime() - offset).toISOString().slice(0, 10)
}

function createAssignmentDraft(): AssignmentDraft {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    partnerNumber: '',
    cityId: '',
    startsOn: localIsoDate(),
  }
}

function ScorePicker({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div>
      <div className="mb-2 text-sm font-medium">{label}</div>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map(option => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(String(option))}
            className={`flex h-9 w-9 items-center justify-center rounded-lg border text-sm font-semibold transition-colors ${
              value === String(option)
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-background hover:bg-muted'
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  )
}

export function PedagogyTeacherCreatePage() {
  const navigate = useNavigate()
  const { loading: accessLoading, canViewPedagogySection, accessError, retryAccessCheck } = usePedagogyAccess()
  const [referenceData, setReferenceData] = useState<TeacherRegistryReferenceData | null>(null)
  const [referenceLoading, setReferenceLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [entryMode, setEntryMode] = useState<TeacherEntryMode>('candidate')

  const [fullName, setFullName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [email, setEmail] = useState('')
  const [contactMatches, setContactMatches] = useState<FranchiseTeacherContactMatch[]>([])
  const [telegramUsername, setTelegramUsername] = useState('')
  const [qualification, setQualification] = useState<TeacherQualification | ''>('')
  const [comment, setComment] = useState('')
  const [assignments, setAssignments] = useState<AssignmentDraft[]>([createAssignmentDraft()])

  const [includeVideo, setIncludeVideo] = useState(false)
  const [evaluateVideo, setEvaluateVideo] = useState(false)
  const [video, setVideo] = useState<VideoDraft>({
    videoUrl: '',
    charismaScore: '',
    professionalismScore: '',
    clarityScore: '',
    emotionalityScore: '',
    decision: '',
    comment: '',
  })

  const [includeContract, setIncludeContract] = useState(false)
  const [contractPartnerNumber, setContractPartnerNumber] = useState('')
  const [contractSignedOn, setContractSignedOn] = useState(localIsoDate())
  const [contractUrl, setContractUrl] = useState('')

  useEffect(() => {
    if (accessLoading) return
    if (!canViewPedagogySection) {
      setReferenceLoading(false)
      return
    }

    let cancelled = false
    setReferenceLoading(true)
    setError('')

    listTeacherRegistryReferenceData()
      .then(data => {
        if (!cancelled) setReferenceData(data)
      })
      .catch(loadError => {
        if (!cancelled) {
          console.error('Failed to load teacher registry reference data:', loadError)
          setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить франчайзи и города')
        }
      })
      .finally(() => {
        if (!cancelled) setReferenceLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [accessLoading, canViewPedagogySection])

  // Занятые почта и телефон — самая частая причина, по которой форму нельзя
  // сохранить, а найти «уже существующего» педагога человек не может: в реестре
  // по умолчанию показаны только работающие. Поэтому спрашиваем до отправки и
  // сразу показываем карточку со ссылкой.
  useEffect(() => {
    if (accessLoading || !canViewPedagogySection) return
    const normalizedEmail = email.trim().toLowerCase()
    const normalizedPhone = phoneNumber.trim()
    if (!normalizedEmail && !normalizedPhone) {
      setContactMatches([])
      return
    }

    let cancelled = false
    const timeoutId = window.setTimeout(() => {
      void findFranchiseTeacherByContact({ email: normalizedEmail, phoneNumber: normalizedPhone })
        .then(matches => { if (!cancelled) setContactMatches(matches) })
        .catch(lookupError => {
          // Подсказка необязательна: сервер всё равно отобьёт дубль тем же правилом.
          if (!cancelled) {
            console.error('Failed to look up teacher by contact:', lookupError)
            setContactMatches([])
          }
        })
    }, 400)

    return () => {
      cancelled = true
      window.clearTimeout(timeoutId)
    }
  }, [accessLoading, canViewPedagogySection, email, phoneNumber])

  const completedAssignments = useMemo(
    () => assignments.filter(item => item.partnerNumber && item.cityId),
    [assignments]
  )

  const selectedPartnerNumbers = useMemo(() => {
    const values = new Set(completedAssignments.map(item => item.partnerNumber))
    return Array.from(values)
  }, [completedAssignments])



  const videoScores = [
    parseScore(video.charismaScore),
    parseScore(video.professionalismScore),
    parseScore(video.clarityScore),
    parseScore(video.emotionalityScore),
  ]
  const videoAverage = videoScores.every((value): value is number => value != null)
    ? videoScores.reduce((sum, value) => sum + value, 0) / videoScores.length
    : null

  useEffect(() => {
    if (contractPartnerNumber && !selectedPartnerNumbers.includes(contractPartnerNumber)) {
      setContractPartnerNumber('')
    }
  }, [contractPartnerNumber, selectedPartnerNumbers])


  const updateAssignment = (id: string, patch: Partial<AssignmentDraft>) => {
    setAssignments(current => current.map(item => {
      if (item.id !== id) return item
      const next = { ...item, ...patch }

      if (patch.cityId !== undefined) {
        const partnerOptions = (referenceData?.assignmentOptions ?? [])
          .filter(option => String(option.cityId) === patch.cityId)
        const uniquePartnerNumbers = Array.from(new Set(partnerOptions.map(option => String(option.partnerNumber))))
        const currentPartnerStillAllowed = uniquePartnerNumbers.includes(item.partnerNumber)
        if (!currentPartnerStillAllowed) {
          next.partnerNumber = uniquePartnerNumbers.length === 1 ? uniquePartnerNumbers[0] : ''
        }
      }

      return next
    }))
  }

  const validate = (): string | null => {
    if (!fullName.trim()) return 'Укажите ФИО педагога'
    if (assignments.length === 0) return 'Добавьте хотя бы одну связь с франчайзи и городом'
    if (assignments.some(item => !item.partnerNumber || !item.cityId)) {
      return 'Для каждой связи выберите франчайзи и город'
    }

    const keys = assignments.map(item => `${item.partnerNumber}:${item.cityId}`)
    if (new Set(keys).size !== keys.length) return 'Одинаковая связь с франчайзи и городом добавлена несколько раз'


    if (includeVideo) {
      if (!/^https?:\/\//i.test(video.videoUrl.trim())) return 'Укажите корректную ссылку на видеовизитку'
      if (evaluateVideo) {
        if (!video.decision) return 'Укажите решение по видеовизитке'
        if (videoScores.some(value => value == null || value < 1 || value > 5)) {
          return 'Все оценки видеовизитки должны быть от 1 до 5'
        }
      }
    }

    if (includeContract) {
      if (!contractPartnerNumber) return 'Выберите франчайзи для договора'
      if (!contractSignedOn) return 'Укажите дату подписания договора'
      if (!/^https?:\/\//i.test(contractUrl.trim())) return 'Укажите корректную ссылку на договор'
    }

    return null
  }

  const handleSubmit = async () => {
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    setSaving(true)
    setError('')
    try {
      const result = await createFranchiseTeacher({
        entryMode,
        fullName,
        phoneNumber,
        email,
        telegramUsername: normalizeTelegramUsername(telegramUsername),
        qualification: qualification || null,
        comment,
        assignments: assignments.map(item => ({
          partnerNumber: Number(item.partnerNumber),
          cityId: Number(item.cityId),
          startsOn: item.startsOn,
        })),
        videoPresentation: includeVideo
          ? {
              partnerNumber: null,
              cityId: null,
              videoUrl: video.videoUrl,
              charismaScore: evaluateVideo ? Number(video.charismaScore) : null,
              professionalismScore: evaluateVideo ? Number(video.professionalismScore) : null,
              clarityScore: evaluateVideo ? Number(video.clarityScore) : null,
              emotionalityScore: evaluateVideo ? Number(video.emotionalityScore) : null,
              decision: evaluateVideo ? video.decision || null : null,
              comment: video.comment,
            }
          : null,
        contract: includeContract
          ? {
              partnerNumber: contractPartnerNumber ? Number(contractPartnerNumber) : null,
              signedOn: contractSignedOn,
              contractUrl,
            }
          : null,
      })

      navigate(pedagogyPaths.teacher(result.teacherId), { replace: true })
    } catch (saveError) {
      console.error('Failed to create franchise teacher:', saveError)
      setError(saveError instanceof Error ? saveError.message : 'Не удалось создать педагога')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } finally {
      setSaving(false)
    }
  }

  if (accessError) return <PedagogyAccessErrorCard onRetry={retryAccessCheck} />
  if (!accessLoading && !canViewPedagogySection) {
    return (
      <div className="space-y-4 pb-24">
        <Card>
          <div className="text-sm text-muted-foreground">У вас нет доступа к созданию педагогов.</div>
        </Card>
      </div>
    )
  }

  if (accessLoading || referenceLoading) {
    return (
      <div className="space-y-4 pb-24">
        <Card><div className="py-10 text-center text-sm text-muted-foreground">Загружаем форму…</div></Card>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-24">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <PedagogyBackLink to={pedagogyPaths.teachers} label="К педагогам" />
          <h1 className="mt-1 text-2xl font-bold tracking-tight">Новый педагог</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Выберите, это новый кандидат или уже действующий педагог, которого нужно внести в реестр.
          </p>
        </div>
      </div>

      {error && (
        <Card className="border border-error/30 bg-error/5">
          <div className="flex items-start gap-2 text-sm text-error">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {contactMatches.length > 0 && (
        <Card className="border border-warning/40 bg-warning/5">
          <div className="flex items-start gap-2">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-warning" />
            <div className="min-w-0 flex-1 space-y-3 text-sm">
              <div className="font-medium">
                {contactMatches.length === 1
                  ? 'Такой педагог уже есть в реестре — новую карточку заводить не нужно'
                  : 'Такие педагоги уже есть в реестре — новые карточки заводить не нужно'}
              </div>
              {contactMatches.map(match => (
                <div key={`${match.teacherId}-${match.matchKind}`} className="rounded-lg border border-border bg-card p-3">
                  <div className="font-medium">{match.fullName}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {match.matchKind === 'email' ? 'Совпала электронная почта' : 'Совпал телефон'}
                    {' · '}{match.statusLabel}
                    {match.assignmentLabel ? ` · ${match.assignmentLabel}` : ''}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <Link
                      to={pedagogyPaths.teacher(match.teacherId)}
                      className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                    >
                      Открыть карточку <ArrowRight size={14} />
                    </Link>
                    {!match.canManage && (
                      <span className="text-xs text-muted-foreground">
                        Изменять карточку может специалист, за которым закреплён этот франчайзи
                      </span>
                    )}
                  </div>
                </div>
              ))}
              <div className="text-xs text-muted-foreground">
                Чтобы перевести педагога к другому франчайзи, откройте карточку и добавьте связь: статус
                «Уволен» снимется автоматически. В списке педагогов уволенные видны только при фильтре
                «Рабочий статус: Все».
              </div>
            </div>
          </div>
        </Card>
      )}

      {!referenceData || referenceData.assignmentOptions.length === 0 ? (
        <Card>
          <div className="py-8 text-center">
            <div className="font-medium">Нет доступных франчайзи</div>
            <div className="mt-1 text-sm text-muted-foreground">
              Проверьте активные студии и связи франчайзи.
            </div>
          </div>
        </Card>
      ) : (
        <>
          <Card>
            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setEntryMode('candidate')}
                className={`rounded-lg border p-4 text-left transition-colors ${
                  entryMode === 'candidate' ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'
                }`}
              >
                <div className="font-semibold">Новый кандидат</div>
                <div className="mt-1 text-sm leading-6 text-muted-foreground">
                  Карточка создаётся кандидатом. Первичное обучение можно запустить только после одобренной видеовизитки.
                </div>
              </button>
              <button
                type="button"
                onClick={() => setEntryMode('active')}
                className={`rounded-lg border p-4 text-left transition-colors ${
                  entryMode === 'active' ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'
                }`}
              >
                <div className="font-semibold">Уже действующий педагог</div>
                <div className="mt-1 text-sm leading-6 text-muted-foreground">
                  Карточка сразу станет working. Система зафиксирует, что история работы и обучения была вне системы.
                </div>
              </button>
            </div>
          </Card>

          <Card className="border border-primary/20 bg-primary/5">
            <div className="flex items-start gap-3">
              <GraduationCap size={20} className="mt-0.5 shrink-0 text-primary" />
              <div>
                <h2 className="font-semibold">{entryMode === 'active' ? 'Исторический педагог без фиктивного обучения' : 'Кандидат без автоматического запуска обучения'}</h2>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  {entryMode === 'active'
                    ? 'Не создаём фиктивные passed-аттестации и не подменяем историю. После сохранения можно добавить реальные документы, отсмотры и при необходимости запустить повторное обучение отдельным действием.'
                    : 'Видеовизитку можно прикрепить и оценить сразу, но решение «Допущен» само по себе не запускает обучение. После создания карточки откройте её и нажмите «Начать обучение».'}
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="mb-4 flex items-center gap-2">
              <UserPlus size={19} className="text-primary" />
              <h2 className="font-semibold">Личные данные</h2>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="md:col-span-2">
                <div className="mb-1.5 text-sm font-medium">ФИО *</div>
                <input value={fullName} onChange={event => setFullName(event.target.value)} className="input w-full" placeholder="Иванова Анна Сергеевна" />
              </label>
              <label>
                <div className="mb-1.5 text-sm font-medium">Телефон</div>
                <input value={phoneNumber} onChange={event => setPhoneNumber(event.target.value)} className="input w-full" placeholder="+7 999 000-00-00" />
              </label>
              <label>
                <div className="mb-1.5 text-sm font-medium">Электронная почта</div>
                <input type="email" value={email} onChange={event => setEmail(event.target.value)} className="input w-full" placeholder="teacher@example.com" />
              </label>
              <label>
                <div className="mb-1.5 text-sm font-medium">Telegram</div>
                <div className="flex">
                  <span className="inline-flex items-center rounded-l-lg border border-r-0 border-border bg-muted/50 px-3 text-sm text-muted-foreground">@</span>
                  <input
                    value={telegramUsername}
                    onChange={event => setTelegramUsername(event.target.value.replace(/^@+/, ''))}
                    className="input w-full rounded-l-none"
                    placeholder="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                </div>
                <div className="mt-1.5 text-xs text-muted-foreground">Введите только username — символ @ уже добавлен.</div>
              </label>
              <label>
                <div className="mb-1.5 text-sm font-medium">Направление</div>
                <select value={qualification} onChange={event => setQualification(event.target.value as TeacherQualification | '')} className="input w-full">
                  <option value="">Не указано</option>
                  <option value="stretch_pilates">Растяжка и пилатес</option>
                  <option value="universal">Тренер-универсал</option>
                </select>
              </label>
              <label className="md:col-span-2">
                <div className="mb-1.5 text-sm font-medium">Комментарий специалиста</div>
                <textarea value={comment} onChange={event => setComment(event.target.value)} rows={3} className="input min-h-24 w-full resize-y" placeholder="Рабочая информация по педагогу" />
              </label>
            </div>
          </Card>

          <Card>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-semibold">Города и франчайзи *</h2>
                <div className="mt-1 text-xs text-muted-foreground">Можно добавить несколько действующих связей.</div>
              </div>
              <button
                type="button"
                onClick={() => setAssignments(current => [...current, createAssignmentDraft()])}
                className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted"
              >
                <Plus size={16} />
                Добавить связь
              </button>
            </div>

            <div className="space-y-3">
              {assignments.map((assignment, index) => {
                const partnerOptions = referenceData.assignmentOptions
                  .filter(option => String(option.cityId) === assignment.cityId)
                  .reduce<Map<number, string>>((map, option) => {
                    map.set(option.partnerNumber, option.partnerName || `Франчайзи №${option.partnerNumber}`)
                    return map
                  }, new Map())

                return (
                  <div key={assignment.id} className="grid gap-3 rounded-xl border border-border p-3 md:grid-cols-[1fr_1fr_180px_auto]">
                    <label>
                      <div className="mb-1.5 text-xs font-medium text-muted-foreground">Город</div>
                      <SearchableSelect
                        value={assignment.cityId}
                        onChange={value => updateAssignment(assignment.id, { cityId: value })}
                        placeholder="Выберите город"
                        searchPlaceholder="Поиск по городу"
                        options={referenceData.cities.map(city => ({
                          value: String(city.cityId),
                          label: city.cityName,
                        }))}
                      />
                    </label>
                    <label>
                      <div className="mb-1.5 text-xs font-medium text-muted-foreground">Франчайзи</div>
                      <SearchableSelect
                        value={assignment.partnerNumber}
                        onChange={value => updateAssignment(assignment.id, { partnerNumber: value })}
                        placeholder={assignment.cityId ? 'Выберите франчайзи' : 'Сначала выберите город'}
                        searchPlaceholder="Поиск по ФИО или номеру"
                        disabled={!assignment.cityId}
                        options={Array.from(partnerOptions.entries())
                          .map(([partnerNumber, partnerName]) => ({
                            value: String(partnerNumber),
                            label: partnerName,
                            searchText: String(partnerNumber),
                          }))
                          .sort((a, b) => a.label.localeCompare(b.label, 'ru'))}
                      />
                    </label>
                    <label>
                      <div className="mb-1.5 text-xs font-medium text-muted-foreground">Дата начала</div>
                      <input
                        type="date"
                        value={assignment.startsOn}
                        onChange={event => updateAssignment(assignment.id, { startsOn: event.target.value })}
                        className="input w-full"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setAssignments(current => current.filter(item => item.id !== assignment.id))}
                      disabled={assignments.length === 1}
                      className="mt-5 inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label={`Удалить связь ${index + 1}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )
              })}
            </div>
          </Card>

          <Card>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Video size={19} className="text-primary" />
                <div>
                  <h2 className="font-semibold">Видеовизитка</h2>
                  <div className="mt-1 text-xs text-muted-foreground">Можно просто прикрепить ссылку, а оценку заполнить позже.</div>
                </div>
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={includeVideo} onChange={event => setIncludeVideo(event.target.checked)} />
                Добавить сейчас
              </label>
            </div>

            {includeVideo && (
              <div className="mt-4 space-y-4 border-t border-border pt-4">
                <label className="block">
                  <div className="mb-1.5 text-sm font-medium">Ссылка на видеовизитку *</div>
                  <input value={video.videoUrl} onChange={event => setVideo(current => ({ ...current, videoUrl: event.target.value }))} className="input w-full" placeholder="https://..." />
                  <div className="mt-1.5 text-xs text-muted-foreground">Видеовизитка относится к педагогу в целом и не привязывается к конкретному франчайзи или городу.</div>
                </label>

                <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={evaluateVideo} onChange={event => setEvaluateVideo(event.target.checked)} />
                  Оценить сразу
                </label>

                {evaluateVideo && (
                  <>
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                      <ScorePicker label="Харизма" value={video.charismaScore} onChange={value => setVideo(current => ({ ...current, charismaScore: value }))} />
                      <ScorePicker label="Профессионализм" value={video.professionalismScore} onChange={value => setVideo(current => ({ ...current, professionalismScore: value }))} />
                      <ScorePicker label="Понятность" value={video.clarityScore} onChange={value => setVideo(current => ({ ...current, clarityScore: value }))} />
                      <ScorePicker label="Эмоциональность" value={video.emotionalityScore} onChange={value => setVideo(current => ({ ...current, emotionalityScore: value }))} />
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <label>
                        <div className="mb-1.5 text-sm font-medium">Решение *</div>
                        <select value={video.decision} onChange={event => setVideo(current => ({ ...current, decision: event.target.value as VideoDraft['decision'] }))} className="input w-full">
                          <option value="">Выберите решение</option>
                          <option value="approved">Допущен</option>
                          <option value="rejected">Не допущен</option>
                        </select>
                        <div className="mt-1.5 text-xs leading-5 text-muted-foreground">
                          «Допущен» означает, что эту визитку можно выбрать при отдельном запуске обучения. Обучение сейчас не начнётся.
                        </div>
                      </label>
                      <div className="rounded-lg bg-muted/50 px-4 py-3">
                        <div className="text-xs text-muted-foreground">Средний балл</div>
                        <div className="mt-1 text-xl font-semibold">{videoAverage == null ? '—' : videoAverage.toFixed(2)}</div>
                      </div>
                    </div>
                  </>
                )}

                <label className="block">
                  <div className="mb-1.5 text-sm font-medium">Комментарий по визитке</div>
                  <textarea value={video.comment} onChange={event => setVideo(current => ({ ...current, comment: event.target.value }))} rows={3} className="input min-h-24 w-full resize-y" />
                </label>
              </div>
            )}
          </Card>

          <Card>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <FileText size={19} className="text-primary" />
                  <div>
                    <h2 className="font-semibold">Договор</h2>
                    <div className="mt-1 text-xs text-muted-foreground">Срок действия будет рассчитан на один год.</div>
                  </div>
                </div>
                <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={includeContract} onChange={event => setIncludeContract(event.target.checked)} />
                  Добавить сейчас
                </label>
              </div>

              {includeContract && (
                <div className="mt-4 grid gap-4 border-t border-border pt-4 md:grid-cols-3">
                  <label>
                    <div className="mb-1.5 text-sm font-medium">Франчайзи *</div>
                    <select value={contractPartnerNumber} onChange={event => setContractPartnerNumber(event.target.value)} className="input w-full">
                      <option value="">Выберите франчайзи</option>
                      {selectedPartnerNumbers.map(partnerNumber => {
                        const partner = referenceData.partners.find(item => String(item.partnerNumber) === partnerNumber)
                        return <option key={partnerNumber} value={partnerNumber}>{partner?.partnerName ?? `Франчайзи №${partnerNumber}`}</option>
                      })}
                    </select>
                  </label>
                  <label>
                    <div className="mb-1.5 text-sm font-medium">Дата подписания *</div>
                    <input type="date" value={contractSignedOn} onChange={event => setContractSignedOn(event.target.value)} className="input w-full" />
                  </label>
                  <label>
                    <div className="mb-1.5 text-sm font-medium">Ссылка на договор *</div>
                    <input value={contractUrl} onChange={event => setContractUrl(event.target.value)} className="input w-full" placeholder="https://..." />
                  </label>
                </div>
              )}
            </Card>

          <div className="sticky bottom-3 z-10 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-background/95 p-3 shadow-lg backdrop-blur">
            <div className="text-sm text-muted-foreground">
              Итоговый статус: <span className="font-medium text-foreground">{entryMode === 'active' ? 'Работает' : 'Кандидат'}</span>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => navigate(pedagogyPaths.teachers)} className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted">
                Отмена
              </button>
              <button
                type="button"
                onClick={() => void handleSubmit()}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Save size={16} />
                {saving ? 'Сохраняем…' : 'Создать педагога'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
